import { useState, useEffect } from 'react';
import { policyAcknowledgmentService } from '@/services/policyAcknowledgmentService';

export const usePolicyAcknowledgment = (userId: string | null, userType: 'supabase' | 'internal' | null) => {
  const [needsAcknowledgment, setNeedsAcknowledgment] = useState(false);
  const [requiresParentalConsent, setRequiresParentalConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [acknowledgmentData, setAcknowledgmentData] = useState<any>(null);

  useEffect(() => {
    if (!userId || !userType) {
      setIsLoading(false);
      return;
    }

    checkStatus();
  }, [userId, userType]);

  const checkStatus = async () => {
    if (!userId || !userType) return;

    setIsLoading(true);
    
    try {
      // Check if acknowledgment is needed
      const { needsAcknowledgment: needs } = await policyAcknowledgmentService.checkAcknowledgmentStatus(userId, userType);
      setNeedsAcknowledgment(needs || false);

      // Check if parental consent is required
      const { required } = await policyAcknowledgmentService.requiresParentalConsent(userId, userType);
      setRequiresParentalConsent(required || false);

      // Get existing acknowledgment data
      const { data } = await policyAcknowledgmentService.getUserAcknowledgment(userId, userType);
      setAcknowledgmentData(data);
    } catch (error) {
      console.error('Error checking policy acknowledgment status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitAcknowledgment = async (data: {
    privacyPolicyAccepted: boolean;
    tosAccepted: boolean;
    cookiesAccepted: boolean;
    dataRightsAcknowledged: boolean;
    parentalConsent?: boolean;
    parentName?: string;
    parentEmail?: string;
  }) => {
    if (!userId || !userType) return { success: false, error: 'User not authenticated' };

    try {
      const result = await policyAcknowledgmentService.submitAcknowledgment({
        userId,
        userType,
        ...data,
        ipAddress: undefined, // Could be collected from client if needed
        userAgent: navigator.userAgent
      });

      if (result.error) {
        return { success: false, error: result.error };
      }

      // Refresh status after submission
      await checkStatus();

      return { success: true, data: result.data };
    } catch (error) {
      console.error('Error submitting acknowledgment:', error);
      return { success: false, error };
    }
  };

  return {
    needsAcknowledgment,
    requiresParentalConsent,
    isLoading,
    acknowledgmentData,
    submitAcknowledgment,
    refreshStatus: checkStatus
  };
};
